import { API_CONFIG, ENDPOINTS } from '@/config/api'
import { authenticatedFetch } from '../authenticatedFetch'

// Types
// Strategic Framework Types (5-Level Hierarchy)
/** A check-in cycle's launch alert (HR UAT list #23). */
export interface CycleLaunchAlert {
  cycle_id: number
  name: string
  review_type: string
  message: string
  checkin_end_date: string
  calibration_end_date: string
  launched_at: string
}

export interface StrategicObjective {
  id: number
  code: string
  name: string
  display_name: string
  description?: string | null
  color_code?: string | null
  icon?: string | null
  vision?: string | null
  target_year?: number | null
  sort_order: number
  is_active: boolean
}

export interface StrategicInitiative {
  id: number
  code: string
  name: string
  description?: string | null
  strategic_objective_id: number
  display_order: number
  status: string
  initiative_type?: string | null
  owner_user_id?: number | null
  start_date?: string | null
  end_date?: string | null
}

export interface OperationalInitiative {
  id: number
  code: string
  name: string
  description?: string | null
  strategic_initiative_id: number
  display_order: number
  is_active: boolean
  owner_user_id?: number | null
  start_date?: string | null
  end_date?: string | null
  target_completion_date?: string | null
}

export interface StrategicInitiativeWithOperational extends StrategicInitiative {
  operational_initiatives?: OperationalInitiative[]
}

export interface StrategicObjectiveWithHierarchy extends StrategicObjective {
  strategic_initiatives?: StrategicInitiativeWithOperational[]
}

export interface StrategicCascade {
  strategic_objectives: StrategicObjectiveWithHierarchy[]
  total_objectives: number
  total_initiatives: number
  total_operational_initiatives: number
}

// Legacy Strategic Initiative Detail (for backwards compatibility)
export interface StrategicInitiativeDetail {
  id: number | string
  name: string
  description?: string | null
  initiative_type: string
  status: string
  owner?: string | null
  start_date?: string | null
  end_date?: string | null
}

export interface IndividualObjectiveDetail {
  id: number
  title: string
  description?: string | null
  role_type: string
  owner: string
  functional_area: string
  status: string
  progress: number
  start_date?: string | null
  end_date?: string | null
  strategic_objective_id: number
  kpa_id: number
  pillar_id: number
  strategic_initiatives?: StrategicInitiativeDetail[]
  kpa?: {  // Full KPA object from performance plan API
    id: number
    name: string
    display_name?: string
    weight_percentage?: number
  }
}

export interface RoleKPI {
  id: string
  name: string
  description: string
  target_value: number
  current_value: number | null
  unit: string
  frequency: string
  status: 'on-track' | 'at-risk' | 'off-track'
  role_type: string
  functional_area: string
  campus: string
  owner_id: string | null
  cascade_to_objective_id: string | null
  cascade_to_kpa_id: string | null
  cascade_to_pillar_id: string | null
  kpa_id?: number | null
  pillar_id?: number | null
  strategic_objective_id?: number | null
  kpa_name: string | null
  pillar_name: string | null
  strategic_objective_name: string | null
  individual_objective_id?: number | null
  primary_initiative_id?: number | null
  individual_objective?: IndividualObjectiveDetail | null
  primary_initiative?: StrategicInitiativeDetail | null
  category: string | null
  owner: string | null
  priority: string | null
  progress_percentage: number | null
  midyear_value?: number | null  // Actual measurement value at mid-year
  yearend_value?: number | null  // Actual measurement value at year-end
  midyear_progress: number  // Progress snapshot at mid-year review (0-100)
  yearend_progress: number  // Progress snapshot at year-end review (0-100)
  trend?: 'up' | 'down' | 'stable' | string | null
  start_date?: string | null
  end_date?: string | null
  is_active: boolean
  milestones: Milestone[]
  created_at: string
  updated_at: string
}

// Employee-specific types
export interface EmployeePerformancePlan {
  user_id: number
  year: number
  has_objectives: boolean
  objectives: EmployeeObjectiveWithKPIs[]
  total_objectives: number
  total_kpis: number
  overall_progress: number
  last_updated: string
  message?: string
}

export interface EmployeeObjectiveWithKPIs {
  id: number
  title: string
  description?: string
  role_type: string
  functional_area: string
  status: string
  progress: number
  start_date?: string | null
  end_date?: string | null
  strategic_objective?: {
    id: number
    name: string
    display_name: string
    code: string
  }
  strategic_initiative?: {
    id: number
    code: string
    name: string
    description: string | null
  }
  operational_initiative?: {
    id: number
    code: string
    name: string
    description: string | null
  }
  kpa?: {
    id: number
    name: string
    display_name?: string
    weight_percentage: number
  }
  pillar?: {
    id: number
    name: string
    color: string
  }
  kpis: EmployeeKPI[]
  kpi_count: number
}

export interface EmployeeKPI {
  id: string
  name: string
  description?: string
  current_value?: number
  target_value: number
  unit: string
  frequency: string
  category: string
  status: string
  trend?: string
  priority?: string
  midyear_progress: number
  yearend_progress: number
  start_date?: string | null
  end_date?: string | null
  milestones: Milestone[]
  // Enterprise measurement configuration
  measurement_direction?: string
  measurement_mode?: string
  rating_scale?: string
  rating_options?: any
  min_value?: number
  max_value?: number
  midyear_value?: number
  yearend_value?: number
  last_measured_at?: string | null
  measurement_history?: any[]
}

export interface EmployeeObjectivesResponse {
  user_id: number
  objectives: {
    id: number
    title: string
    description?: string
    status: string
    progress: number
    role_type: string
    functional_area: string
  }[]
  total: number
}

export interface EmployeeKPIsResponse {
  user_id: number
  kpis: {
    id: string
    name: string
    description?: string
    current_value?: number
    target_value: number
    unit: string
    status: string
    category: string
    frequency: string
    midyear_progress: number
    yearend_progress: number
  }[]
  total: number
}

export interface Milestone {
  id: string
  title: string
  description?: string
  due_date: string
  milestone_type: 'strategic' | 'operational' | 'individual' | 'review_formal' | 'review_informal' | 'review_jit' | 'idp_action'
  status: 'pending' | 'in-progress' | 'completed' | 'overdue'
  progress: number
  created_at: string
  updated_at: string
}

export interface ReviewMilestone {
  id: string
  title: string
  description?: string
  due_date: string
  status: string
  milestone_type: string
  review_type?: string
  progress: number
  user_id?: string
  manager_id?: string
  requires_rating: boolean
  requires_calibration: boolean
  requires_anonymous_feedback: boolean
  review_session_id?: string
  created_at: string
  updated_at: string
}

export interface ReviewMilestonesResponse {
  user_id: string
  review_year: number
  milestones: ReviewMilestone[]
  total_reviews: number
  formal_reviews: number
  informal_reviews: number
  completed_reviews: number
  upcoming_reviews: number
}

export interface KPIMeasurement {
  id: string
  value: number
  measurement_date: string
  notes?: string
}

export interface StrategicPillar {
  id: string
  name: string
  description: string
  kpas: KPA[]
}

export interface KPA {
  id: number
  name: string
  display_name?: string | null
  description?: string | null
  strategic_pillar_id: number
  sort_order?: number | null
  weight_percentage?: number | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface Objective {
  id: string
  title: string
  description: string
  kpa_id: string
  status: 'pending' | 'in-progress' | 'completed'
}

/** One of last year's objectives, offered for pulling into the next year (HR UAT list #27). */
export interface PriorYearObjective {
  id: number
  title: string
  description: string | null
  year: number | null
  status: string
  kpa: { id: number; name: string; display_name: string } | null
  kpis: Array<{
    id: string
    name: string
    description: string | null
    target_value: number | null
    unit: string | null
    frequency: string | null
    measurement_mode?: string | null
  }>
  /** Already copied into the year being set up. */
  already_added: boolean
}

// API Service Class
class PerformanceService {
  private baseURL = API_CONFIG.baseURL

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await authenticatedFetch(`${this.baseURL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options?.headers as Record<string, string>),
      },
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    if (response.status === 204) {
      return null as T
    }

    return response.json()
  }

  // ── Review-cycle boundary (the date each year YEAREND begins) ──
  // Read-only: the boundary is DERIVED from the Year-End cycle's start_date; move it
  // by editing that cycle via the admin cycles endpoints, not by PUTting here.
  async getReviewCycleConfig(): Promise<{ h2_start_month: number; h2_start_day: number; yearend_starts: string }> {
    return this.request('/api/v1/performance/review-cycle-config')
  }

  /** The alert for a check-in cycle HR has opened, when the signed-in person is in it (HR UAT list #23). */
  async getCycleLaunchAlert(): Promise<{ alert: CycleLaunchAlert | null }> {
    return this.request('/api/v1/performance/cycle-launch-alert')
  }

  // Role-based KPI endpoints
  async getRoleKPIs(roleType: string): Promise<RoleKPI[]> {
    const response = await this.request<{role_data: {kpis: RoleKPI[]}}>(ENDPOINTS.roleKpis.list(roleType))
    return response.role_data.kpis
  }

  async createRoleKPI(roleType: string, kpi: Omit<RoleKPI, 'id'>): Promise<RoleKPI> {
    return this.request<RoleKPI>(ENDPOINTS.roleKpis.create(roleType), {
      method: 'POST',
      body: JSON.stringify(kpi),
    })
  }

  async updateRoleKPI(roleType: string, id: string, kpi: Partial<RoleKPI>): Promise<RoleKPI> {
    return this.request<RoleKPI>(ENDPOINTS.roleKpis.update(roleType, id), {
      method: 'PUT',
      body: JSON.stringify(kpi),
    })
  }

  // Standard KPI endpoints
  async getKPIs(): Promise<RoleKPI[]> {
    return this.request<RoleKPI[]>(ENDPOINTS.kpis.list)
  }

  async getKPI(id: string): Promise<RoleKPI> {
    return this.request<RoleKPI>(ENDPOINTS.kpis.get(id))
  }

  async createKPI(kpi: Omit<RoleKPI, 'id'>): Promise<RoleKPI> {
    return this.request<RoleKPI>(ENDPOINTS.kpis.create, {
      method: 'POST',
      body: JSON.stringify(kpi),
    })
  }

  async updateKPI(id: string, kpi: Partial<RoleKPI>): Promise<RoleKPI> {
    return this.request<RoleKPI>(ENDPOINTS.kpis.update(id), {
      method: 'PUT',
      body: JSON.stringify(kpi),
    })
  }

  async deleteKPI(id: string): Promise<void> {
    return this.request<void>(ENDPOINTS.kpis.delete(id), {
      method: 'DELETE',
    })
  }

  // Strategic Pillars
  async getStrategicPillars(): Promise<StrategicPillar[]> {
    return this.request<StrategicPillar[]>(ENDPOINTS.strategicPillars.list)
  }

  async createStrategicPillar(pillar: Omit<StrategicPillar, 'id'>): Promise<StrategicPillar> {
    return this.request<StrategicPillar>(ENDPOINTS.strategicPillars.create, {
      method: 'POST',
      body: JSON.stringify(pillar),
    })
  }

  // Key Performance Areas
  async getKPAs(): Promise<KPA[]> {
    const response = await this.request<{ kpas: KPA[]; total: number; skip: number; limit: number }>(ENDPOINTS.kpas.list)
    return response.kpas
  }

  async createKPA(kpa: Omit<KPA, 'id'>): Promise<KPA> {
    return this.request<KPA>(ENDPOINTS.kpas.create, {
      method: 'POST',
      body: JSON.stringify(kpa),
    })
  }

  // Objectives
  async getObjectives(): Promise<Objective[]> {
    return this.request<Objective[]>(ENDPOINTS.objectives.list)
  }

  async createObjective(objective: Omit<Objective, 'id'>): Promise<Objective> {
    return this.request<Objective>(ENDPOINTS.objectives.create, {
      method: 'POST',
      body: JSON.stringify(objective),
    })
  }

  // Employee-initiated objective creation
  async createEmployeeObjective(userId: string, objectiveData: {
    title: string
    description: string
    kpa_id: string
    start_date?: string
    end_date?: string
    kpis: Array<{
      name: string
      description?: string
      target?: string
      unit?: string
      frequency?: string
    }>
    created_by_manager?: boolean
    // Strategic context (L1-L3) - optional
    strategic_objective_id?: number
    strategic_initiative_id?: number
    operational_initiative_id?: number
    // Real AI SMART analysis to persist (from wizard or client-side template analysis)
    smart_analysis?: any
    // The performance year it is for: dates left out cover the year, and dates sent must
    // fall inside it (HR UAT list #27)
    performance_year?: number
  }): Promise<any> {
    // Extract created_by_manager flag if present (pass as query param, not in body)
    const { created_by_manager, ...bodyData } = objectiveData
    const queryParam = created_by_manager ? '?created_by_manager=true' : ''
    
    return this.request(`/api/v1/employees/${userId}/objectives/${queryParam}`, {
      method: 'POST',
      body: JSON.stringify(bodyData),
    })
  }

  // Cascade visualization
  async getCascadeVisualization(): Promise<any> {
    return this.request<any>(ENDPOINTS.cascade.visualization)
  }

  async getKPIToPillarPath(kpiId: string): Promise<any> {
    return this.request<any>(ENDPOINTS.cascade.kpiToPillar(kpiId))
  }

  // KPI Measurements
  async getKPIMeasurements(kpiId: string): Promise<KPIMeasurement[]> {
    return this.request<KPIMeasurement[]>(ENDPOINTS.kpis.measurements(kpiId))
  }

  async addKPIMeasurement(kpiId: string, measurement: Omit<KPIMeasurement, 'id'>): Promise<KPIMeasurement> {
    return this.request<KPIMeasurement>(ENDPOINTS.kpis.measurements(kpiId), {
      method: 'POST',
      body: JSON.stringify(measurement),
    })
  }

  // KPI Milestones
  async getKPIMilestones(kpiId: string): Promise<Milestone[]> {
    return this.request<Milestone[]>(ENDPOINTS.kpis.milestones(kpiId))
  }

  async addKPIMilestone(kpiId: string, milestone: Omit<Milestone, 'id' | 'status' | 'progress' | 'created_at' | 'updated_at'>): Promise<Milestone> {
    return this.request<Milestone>(ENDPOINTS.kpis.milestones(kpiId), {
      method: 'POST',
      body: JSON.stringify(milestone),
    })
  }

  async updateKPIMilestone(kpiId: string, milestoneId: string, milestone: Partial<Milestone>): Promise<Milestone> {
    return this.request<Milestone>(`${ENDPOINTS.kpis.milestones(kpiId)}/${milestoneId}`, {
      method: 'PUT',
      body: JSON.stringify(milestone),
    })
  }

  async deleteKPIMilestone(kpiId: string, milestoneId: string): Promise<void> {
    return this.request<void>(`${ENDPOINTS.kpis.milestones(kpiId)}/${milestoneId}`, {
      method: 'DELETE',
    })
  }

  // Review Cycle Milestones
  async getUserReviewMilestones(userId: string, year?: number, includeCompleted: boolean = true): Promise<ReviewMilestonesResponse> {
    const params = new URLSearchParams()
    if (year) params.append('year', year.toString())
    params.append('include_completed', includeCompleted.toString())
    
    const endpoint = `${ENDPOINTS.reviews.userReviews(userId)}?${params.toString()}`
    return this.request<ReviewMilestonesResponse>(endpoint)
  }

  async getNextReviewMilestone(userId: string): Promise<ReviewMilestone> {
    return this.request<ReviewMilestone>(ENDPOINTS.reviews.nextReview(userId))
  }

  async getTeamReviewMilestones(managerId: string, year?: number, status?: string): Promise<ReviewMilestone[]> {
    const params = new URLSearchParams()
    if (year) params.append('year', year.toString())
    if (status) params.append('status', status)
    
    const endpoint = `${ENDPOINTS.reviews.teamReviews(managerId)}?${params.toString()}`
    return this.request<ReviewMilestone[]>(endpoint)
  }

  // ===== Employee-Specific Performance Plan Endpoints =====
  
  /**
   * Get complete performance plan for an employee.
   * This is the primary endpoint for the Employee Journey page.
   */
  async getEmployeePerformancePlan(userId: string | number, year?: number, includeCompleted: boolean = false): Promise<EmployeePerformancePlan> {
    const params = new URLSearchParams()
    if (year) params.append('year', year.toString())
    params.append('include_completed', includeCompleted.toString())
    
    const endpoint = `/api/v1/employees/${userId}/performance-plan?${params.toString()}`
    return this.request<EmployeePerformancePlan>(endpoint)
  }

  /**
   * Get objectives assigned to an employee (simplified).
   */
  async getEmployeeObjectives(
    userId: string | number, 
    options?: { 
      status?: string
      year?: number 
    }
  ): Promise<EmployeeObjectivesResponse> {
    const params = new URLSearchParams()
    if (options?.status) params.append('status', options.status)
    if (options?.year) params.append('year', options.year.toString())
    
    const endpoint = `/api/v1/employees/${userId}/objectives?${params.toString()}`
    return this.request<EmployeeObjectivesResponse>(endpoint)
  }

  /**
   * Get KPIs assigned to an employee.
   */
  async getEmployeeKPIs(userId: number, status?: string, category?: string): Promise<EmployeeKPIsResponse> {
    const params = new URLSearchParams()
    if (status) params.append('status', status)
    if (category) params.append('category', category)
    
    const endpoint = `/api/v1/employees/${userId}/kpis?${params.toString()}`
    return this.request<EmployeeKPIsResponse>(endpoint)
  }

  /**
   * Assign an objective to an employee (Manager action).
   */
  async assignObjectiveToEmployee(
    userId: number, 
    objectiveId: number, 
    assignedByUserId: number
  ): Promise<{ message: string; individual_objective_id: number }> {
    const params = new URLSearchParams()
    params.append('assigned_by_user_id', assignedByUserId.toString())
    
    const endpoint = `/api/v1/employees/${userId}/objectives/${objectiveId}/assign?${params.toString()}`
    return this.request(endpoint, { method: 'POST' })
  }

  /**
   * Assign a KPI to an employee (Manager action).
   */
  async assignKPIToEmployee(
    userId: number,
    kpiId: string,
    assignedByUserId: number,
    individualObjectiveId?: number
  ): Promise<{ message: string }> {
    const params = new URLSearchParams()
    params.append('assigned_by_user_id', assignedByUserId.toString())
    if (individualObjectiveId) params.append('individual_objective_id', individualObjectiveId.toString())
    
    const endpoint = `/api/v1/employees/${userId}/kpis/${kpiId}/assign?${params.toString()}`
    return this.request(endpoint, { method: 'POST' })
  }

  /**
   * Remove an objective assignment from an employee (Manager action).
   */
  async removeObjectiveFromEmployee(
    userId: number,
    objectiveId: number,
    removedByUserId: number
  ): Promise<{ message: string }> {
    const params = new URLSearchParams()
    params.append('removed_by_user_id', removedByUserId.toString())
    
    const endpoint = `/api/v1/employees/${userId}/objectives/${objectiveId}?${params.toString()}`
    return this.request(endpoint, { method: 'DELETE' })
  }

  // Evidence endpoints
  async getUserEvidence(
    userId: string,
    filters?: {
      kpaId?: string
      kpiId?: string
      objectiveId?: string
      source?: string
      classification?: string
      reviewPeriod?: string
      /**
       * Filter by the performance year of the entry. Backend matches
       * against EXTRACT(YEAR FROM evidence_date). Required to keep
       * cross-year leakage out of mid-year / year-end views — without it,
       * 2026 MIDYEAR entries surface in a 2025 MIDYEAR view (same period
       * label, different year).
       */
      year?: number
      skip?: number
      limit?: number
    }
  ): Promise<any> {
    const params = new URLSearchParams()
    if (filters?.kpaId) params.append('kpa_id', filters.kpaId)
    if (filters?.kpiId) params.append('kpi_id', filters.kpiId)
    if (filters?.objectiveId) params.append('objective_id', filters.objectiveId)
    if (filters?.source) params.append('source', filters.source)
    if (filters?.classification) params.append('classification', filters.classification)
    if (filters?.reviewPeriod) params.append('review_period', filters.reviewPeriod)
    if (filters?.year !== undefined) params.append('year', filters.year.toString())
    if (filters?.skip !== undefined) params.append('skip', filters.skip.toString())
    if (filters?.limit !== undefined) params.append('limit', filters.limit.toString())

    const queryString = params.toString()
    const endpoint = `${ENDPOINTS.evidence.list(userId)}${queryString ? `?${queryString}` : ''}`
    return this.request(endpoint)
  }

  async createEvidence(userId: string, evidence: any): Promise<any> {
    return this.request(ENDPOINTS.evidence.create(userId), {
      method: 'POST',
      body: JSON.stringify(evidence)
    })
  }

  async getEvidence(userId: string, evidenceId: string): Promise<any> {
    return this.request(ENDPOINTS.evidence.get(userId, evidenceId))
  }

  async updateEvidence(userId: string, evidenceId: string, updates: any): Promise<any> {
    return this.request(ENDPOINTS.evidence.update(userId, evidenceId), {
      method: 'PUT',
      body: JSON.stringify(updates)
    })
  }

  async updateEvidenceClassification(
    userId: string,
    evidenceId: string,
    classification: string
  ): Promise<any> {
    return this.request(ENDPOINTS.evidence.updateClassification(userId, evidenceId), {
      method: 'PATCH',
      body: JSON.stringify({ classification })
    })
  }

  async deleteEvidence(userId: string, evidenceId: string): Promise<void> {
    return this.request(ENDPOINTS.evidence.delete(userId, evidenceId), {
      method: 'DELETE'
    })
  }

  /**
   * Carry a fully-locked Mid-Year evidence forward into a fresh editable
   * Year-End entry. Backend reference-copies attachments, drops the KPI
   * link if it's been retired, and returns the new evidence row plus an
   * optional `kpi_carry_forward_warning` flag. Idempotent — calling
   * twice on the same source returns the existing derivative.
   */
  async carryForwardEvidence(
    userId: string,
    evidenceId: string,
  ): Promise<{
    evidence: any
    kpi_carry_forward_warning: 'kpi_no_longer_active' | null
    idempotent: boolean
  }> {
    return this.request(
      `/performance/evidence/users/${userId}/evidence/${evidenceId}/carry-forward`,
      { method: 'POST' },
    )
  }

  // -------- A2: Per-entry release model (May 2026 rework) --------
  //
  // Binary release model (June 2026). Each evidence row is private to its
  // author by default; the team member shares it via `releaseEvidence` and
  // makes it private again via `withdrawEvidence`. Shared = visible to the
  // People Leader in every check-in for the entry's period (capture period +
  // carry-forward decide which period — NOT the session). Backend returns the
  // updated evidence row (with `isReleased` / `lockState` populated) so the
  // caller can patch local state without re-fetching.

  /**
   * Share an evidence entry with the team member's People Leader (binary).
   * Only the portfolio owner can share their own entries. Refused with 409
   * once the entry's period has finalised. Measurements are always shared and
   * don't use this path.
   *
   * Returns the updated evidence row so the caller can update local state.
   */
  async releaseEvidence(
    userId: string,
    evidenceId: string,
  ): Promise<{ evidence: any }> {
    return this.request(
      `/performance/evidence/users/${userId}/evidence/${evidenceId}/release`,
      { method: 'POST' },
    )
  }

  /**
   * Un-share an evidence entry (set it back to private). Binary release.
   * Refuses with 409 once the entry's period is finalised.
   */
  async withdrawEvidence(
    userId: string,
    evidenceId: string,
  ): Promise<{ evidence: any }> {
    return this.request(
      `/performance/evidence/users/${userId}/evidence/${evidenceId}/withdraw`,
      { method: 'POST' },
    )
  }

  // Employee KPI Measurement endpoints
  async recordEmployeeKPIMeasurement(
    employeeId: string,
    kpiId: string, 
    measurement: {
      value: number
      notes?: string
      evidence_id?: string
      measurement_date?: string
      period?: string  // Review period: MIDYEAR or YEAREND
    }
  ): Promise<any> {
    return this.request(`/api/v1/employees/${employeeId}/kpis/${kpiId}/measurements`, {
      method: 'POST',
      body: JSON.stringify(measurement)
    })
  }

  async getEmployeeKPIProgress(employeeId: string, kpiId: string, year?: number): Promise<any> {
    const yearParam = year ? `?year=${year}` : ''
    return this.request(`/api/v1/employees/${employeeId}/kpis/${kpiId}/progress${yearParam}`)
  }

  // Attachment endpoints
  async uploadAttachment(evidenceId: string, file: File): Promise<any> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await authenticatedFetch(
      `${this.baseURL}/api/v1/performance/evidence/${evidenceId}/attachments`,
      {
        method: 'POST',
        body: formData,
        // No Content-Type — browser sets it with multipart boundary for FormData
      }
    )

    if (!response.ok) {
      throw new Error(`Failed to upload attachment: ${response.statusText}`)
    }

    return response.json()
  }

  async listAttachments(evidenceId: string): Promise<any> {
    return this.request(`/api/v1/performance/evidence/${evidenceId}/attachments`)
  }

  async deleteAttachment(attachmentId: string): Promise<void> {
    return this.request(`/api/v1/performance/attachments/${attachmentId}`, {
      method: 'DELETE'
    })
  }

  getAttachmentDownloadUrl(attachmentId: string): string {
    return `${this.baseURL}/api/v1/performance/attachments/${attachmentId}/download`
  }

  getAttachmentViewUrl(attachmentId: string): string {
    return `${this.baseURL}/api/v1/performance/attachments/${attachmentId}/download?inline=true`
  }

  /**
   * Download attachment with auth headers and trigger browser download
   */
  async downloadAttachment(attachmentId: string, fileName?: string): Promise<void> {
    const url = `/api/v1/performance/attachments/${attachmentId}/download`
    const response = await authenticatedFetch(`${this.baseURL}${url}`)

    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`)
    }

    const blob = await response.blob()
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = fileName || 'attachment'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  }

  /**
   * View attachment inline (PDF, images) with auth headers
   */
  async viewAttachmentInline(attachmentId: string): Promise<string> {
    const url = `/api/v1/performance/attachments/${attachmentId}/download?inline=true`
    const response = await authenticatedFetch(`${this.baseURL}${url}`)

    if (!response.ok) {
      throw new Error(`View failed: ${response.status}`)
    }

    const blob = await response.blob()
    return window.URL.createObjectURL(blob)
  }

  // Get SAS URL for Office Online viewing (returns promise with office_viewer_url)
  async getOfficeViewerUrl(attachmentId: string): Promise<string> {
    const response = await this.request<{ sas_url: string; office_viewer_url: string; expires_in_hours: number }>(
      `/api/v1/performance/attachments/${attachmentId}/sas-url`
    )
    return response.office_viewer_url
  }

  // Check if file type can be viewed inline in browser (PDF, images, text)
  canViewInline(fileType: string): boolean {
    const inlineTypes = [
      'application/pdf',
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp',
      'text/plain', 'text/csv', 'text/html',
    ]
    return inlineTypes.includes(fileType?.toLowerCase() || '')
  }

  // Check if file type can be viewed with Office Online viewer
  canViewWithOffice(fileType: string): boolean {
    const officeTypes = [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ]
    return officeTypes.includes(fileType?.toLowerCase() || '')
  }

  // Individual Objective Approval/Rejection endpoints
  async approveIndividualObjective(
    objectiveId: number, 
    approvalData: {
      feedback?: string
      edits?: {
        title?: string
        description?: string
        start_date?: string
        end_date?: string
        kpis?: Array<{
          id?: string
          name: string
          description?: string
          target_value: number
          current_value?: number
          unit: string
          frequency: string
          category?: string
          status?: string
          priority?: string
          start_date?: string
          end_date?: string
        }>
      }
    }
  ): Promise<any> {
    return this.request(`/api/v1/objectives/individual/${objectiveId}/approve`, {
      method: 'PUT',
      body: JSON.stringify(approvalData)
    })
  }

  async rejectIndividualObjective(
    objectiveId: number,
    reason: string
  ): Promise<any> {
    return this.request(`/api/v1/objectives/individual/${objectiveId}/reject`, {
      method: 'PUT',
      body: JSON.stringify({ reason })
    })
  }

  async updateIndividualObjective(
    objectiveId: number,
    updates: {
      title?: string
      description?: string
      start_date?: string
      end_date?: string
      edited_by_role?: 'employee' | 'manager'
      // L1-L3 Strategic Context
      strategic_objective_id?: number | null
      strategic_initiative_id?: number | null
      operational_initiative_id?: number | null
      kpis?: Array<{
        id?: string
        name: string
        description?: string
        target_value: number
        current_value?: number
        unit: string
        frequency: string
        category?: string
        status?: string
        priority?: string
        start_date?: string
        end_date?: string
      }>
    }
  ): Promise<any> {
    return this.request(`/api/v1/objectives/individual/${objectiveId}/update`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    })
  }

  // ==================== Objective Allocation APIs ====================

  /**
   * Get all templates including drafts (single consolidated template source)
   */
  async getAllTemplates(params?: {
    limit?: number
    status?: string
    created_by?: string
  }): Promise<{ items: any[] }> {
    const queryParams = new URLSearchParams()
    if (params?.limit) queryParams.append('limit', String(params.limit))
    if (params?.status) queryParams.append('status', params.status)
    if (params?.created_by) queryParams.append('created_by', params.created_by)
    
    const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
    return this.request(`/api/v1/performance/templates${query}`)
  }

  /**
   * Create a new objective template
   */
  async createObjectiveTemplate(templateData: {
    name: string
    description: string
    objectives_data: {
      objectives: Array<{
        title: string
        description: string
        kpa?: {
          id: string
          name: string
          display_name: string
          sort_order: number
        }
        kpa_id?: number
        kpa_name?: string
        pillar_name?: string
        kpis?: Array<{
          id: string
          name: string
          description: string
          target_value: number
          current_value: number
          unit: string
          frequency: string
          category: string
          status: string
          priority: string
          start_date?: string
          end_date?: string
        }> | string[]
        kpi_count?: number
        role_type?: string
        status?: string
        progress?: number
      }>
    }
    template_type?: string
    role_type?: string
  }): Promise<any> {
    return this.request('/api/v1/performance/templates', {
      method: 'POST',
      body: JSON.stringify(templateData)
    })
  }

  /**
   * The objectives from the year before `forYear` that can be pulled into it (the ones that
   * ran), each with its KPIs and whether it is already in `forYear` (HR UAT list #27).
   */
  async getPriorYearObjectives(employeeId: string, forYear: number): Promise<PriorYearObjective[]> {
    const params = new URLSearchParams({ for_year: String(forYear), year: String(forYear - 1) })
    return this.request(`/api/v1/objectives/prior-year/${employeeId}?${params.toString()}`)
  }

  /**
   * Copy chosen objectives into a year for one person, each pending approval and with the KPIs
   * chosen for it: from their objectives of the year before, or from HR's Objective Library.
   * Ones already copied into the year are left out and listed in `already_added` (HR UAT
   * list #27).
   */
  async copyObjectives(request: {
    employee_id: string
    year: number
    source: 'prior_year' | 'library'
    objective_ids: number[]
    /** The KPIs to bring, by objective id, at least one each; an objective not named brings all. */
    kpi_ids?: Record<string, string[]>
  }): Promise<{ year: number; created: Array<{ id: number; title: string; source_id: number }>; already_added: number[] }> {
    return this.request('/api/v1/objectives/copy', {
      method: 'POST',
      body: JSON.stringify(request)
    })
  }

  // ==================== Collaborative Session APIs ====================

  /**
   * Create a new collaborative session for objective setting
   */
  async createCollaborativeSession(request: {
    employee_id: string
    employee_name: string
    employee_role?: string
    manager_id: string
    manager_name: string
    session_type?: string
    performance_year?: number
    scheduled_by?: string  // 'system' or manager ID
  }): Promise<any> {
    return this.request('/api/v1/collaborative/sessions', {
      method: 'POST',
      body: JSON.stringify(request)
    })
  }

  /**
   * Get a specific collaborative session
   */
  async getCollaborativeSession(sessionId: string): Promise<any> {
    return this.request(`/api/v1/collaborative/sessions/${sessionId}`)
  }

  /**
   * Get all collaborative sessions for a manager
   */
  async getManagerSessions(
    managerId: string,
    params?: {
      status?: string
      performance_year?: number
    }
  ): Promise<any[]> {
    const queryParams = new URLSearchParams()
    if (params?.status) queryParams.append('status', params.status)
    if (params?.performance_year) queryParams.append('performance_year', String(params.performance_year))
    
    const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
    return this.request(`/api/v1/collaborative/managers/${managerId}/sessions${query}`)
  }

  /**
   * Get all collaborative sessions for an employee
   */
  async getEmployeeSessions(
    employeeId: string,
    params?: {
      performance_year?: number
    }
  ): Promise<any[]> {
    const queryParams = new URLSearchParams()
    if (params?.performance_year) queryParams.append('performance_year', String(params.performance_year))
    
    const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
    return this.request(`/api/v1/collaborative/employees/${employeeId}/sessions${query}`)
  }

  /**
   * Schedule a collaborative session with date and meeting details
   */
  async scheduleCollaborativeSession(
    sessionId: string,
    managerId: string,
    scheduleData: {
      scheduled_date: string
      meeting_type: string
      meeting_location: string
      meeting_link?: string
      meeting_agenda?: string
    }
  ): Promise<any> {
    return this.request(`/api/v1/collaborative/sessions/${sessionId}/schedule?manager_id=${managerId}`, {
      method: 'PATCH',
      body: JSON.stringify(scheduleData)
    })
  }

  /**
   * Update session outcomes during or after the meeting
   */
  async updateSessionOutcomes(
    sessionId: string,
    outcomes: {
      role_profile_confirmed?: boolean
      strategy_cascade_reviewed?: boolean
      objectives_discussed?: boolean
      objectives_finalized?: boolean
      collaboration_notes?: string
      employee_feedback?: string
      action_items?: any[]
      objectives_created?: string[]
    }
  ): Promise<any> {
    return this.request(`/api/v1/collaborative/sessions/${sessionId}/outcomes`, {
      method: 'PATCH',
      body: JSON.stringify(outcomes)
    })
  }

  /**
   * Mark a collaborative session as completed
   */
  async completeCollaborativeSession(
    sessionId: string,
    completion: {
      collaboration_notes?: string
      employee_feedback?: string
      objectives_created?: string[]
      duration_minutes?: number
    }
  ): Promise<any> {
    return this.request(`/api/v1/collaborative/sessions/${sessionId}/complete`, {
      method: 'PATCH',
      body: JSON.stringify(completion)
    })
  }

  /**
   * Cancel a collaborative session
   */
  async cancelCollaborativeSession(
    sessionId: string,
    reason?: string
  ): Promise<any> {
    const query = reason ? `?reason=${encodeURIComponent(reason)}` : ''
    return this.request(`/api/v1/collaborative/sessions/${sessionId}/cancel${query}`, {
      method: 'PATCH'
    })
  }

  /**
   * Approve an objective (manager only)
   */
  async approveObjective(
    objectiveId: number,
    options?: {
      feedback?: string
      edits?: any
    }
  ): Promise<any> {
    return this.request(`/api/v1/objectives/individual/${objectiveId}/approve`, {
      method: 'PUT',
      body: JSON.stringify(options || {})
    })
  }

  /**
   * Reject an objective (manager only)
   */
  async rejectObjective(
    objectiveId: number,
    options: {
      reason: string
    }
  ): Promise<any> {
    return this.request(`/api/v1/objectives/individual/${objectiveId}/reject`, {
      method: 'PUT',
      body: JSON.stringify(options)
    })
  }

  /**
   * Get change history for an objective
   */
  async getObjectiveChanges(objectiveId: number): Promise<any[]> {
    return this.request(`/api/v1/objectives/individual/${objectiveId}/changes`)
  }

  /**
   * Update an employee objective
   */
  async updateEmployeeObjective(
    userId: string,
    objectiveId: number,
    updates: any
  ): Promise<any> {
    return this.request(`/api/v1/objectives/individual/${objectiveId}/update`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    })
  }

  /**
   * Delete an objective
   */
  async deleteObjective(objectiveId: number): Promise<any> {
    return this.request(`/api/v1/objectives/individual/${objectiveId}`, {
      method: 'DELETE'
    })
  }

  /**
   * Withdraw a pending objective (soft-delete with 'withdrawn' status)
   * Only allowed if collaborative session hasn't been scheduled yet
   */
  async withdrawObjective(objectiveId: number, withdrawnBy: 'employee' | 'manager'): Promise<any> {
    return this.request(`/api/v1/objectives/individual/${objectiveId}/withdraw?withdrawn_by=${withdrawnBy}`, {
      method: 'PUT'
    })
  }

  /**
   * Get library objectives (institutional objective library)
   */
  async getLibraryObjectives(options?: {
    limit?: number
    expand?: string
    library_only?: boolean
  }): Promise<any[]> {
    const params = new URLSearchParams()
    if (options?.limit) params.append('limit', options.limit.toString())
    if (options?.expand) params.append('expand', options.expand)
    if (options?.library_only !== undefined) params.append('library_only', options.library_only.toString())
    
    const queryString = params.toString()
    return this.request(`/api/v1/objectives/individual/library${queryString ? `?${queryString}` : ''}`)
  }

  /**
   * The objectives of the year before `forYear` that ran, for each of a People Leader's direct
   * reports: each person's own prior-year list (getPriorYearObjectives, which their People Leader
   * may read), with how many people's lists couldn't be read (HR UAT list #27).
   */
  async getPriorYearObjectivesForTeam(managerId: string, forYear: number): Promise<{
    lists: PriorYearObjective[][]
    unreadable: number
  }> {
    const teamSummary = await this.request<any>(`/api/v1/manager/${managerId}/team-summary`)
    const members: any[] = teamSummary.team_members || []
    const read = await Promise.allSettled(
      members.map(member => this.getPriorYearObjectives(member.employee_id, forYear))
    )
    return {
      lists: read.flatMap(r => (r.status === 'fulfilled' ? [r.value] : [])),
      unreadable: read.filter(r => r.status === 'rejected').length,
    }
  }

  // ========================================
  // Strategic Framework (5-Level Hierarchy)
  // ========================================

  /**
   * Get all strategic objectives (Level 1)
   */
  async getStrategicObjectives(activeOnly: boolean = true): Promise<StrategicObjective[]> {
    const params = activeOnly ? '?active_only=true' : '?active_only=false'
    return this.request(`/api/v1/strategic-framework/strategic-objectives${params}`)
  }

  /**
   * Get a specific strategic objective with optional hierarchy
   */
  async getStrategicObjective(
    objectiveId: number,
    includeHierarchy: boolean = true
  ): Promise<StrategicObjectiveWithHierarchy> {
    const params = includeHierarchy ? '?include_hierarchy=true' : '?include_hierarchy=false'
    return this.request(`/api/v1/strategic-framework/strategic-objectives/${objectiveId}${params}`)
  }

  /**
   * Get all strategic initiatives (Level 2)
   */
  async getStrategicInitiatives(
    strategicObjectiveId?: number,
    status?: string
  ): Promise<StrategicInitiative[]> {
    const params = new URLSearchParams()
    if (strategicObjectiveId) params.append('strategic_objective_id', strategicObjectiveId.toString())
    if (status) params.append('status', status)
    const queryString = params.toString()
    return this.request(`/api/v1/strategic-framework/strategic-initiatives${queryString ? `?${queryString}` : ''}`)
  }

  /**
   * Get a specific strategic initiative with operational initiatives
   */
  async getStrategicInitiative(
    initiativeId: number,
    includeOperational: boolean = true
  ): Promise<StrategicInitiativeWithOperational> {
    const params = includeOperational ? '?include_operational=true' : '?include_operational=false'
    return this.request(`/api/v1/strategic-framework/strategic-initiatives/${initiativeId}${params}`)
  }

  /**
   * Get all operational initiatives (Level 3)
   */
  async getOperationalInitiatives(
    strategicInitiativeId?: number,
    activeOnly: boolean = true
  ): Promise<OperationalInitiative[]> {
    const params = new URLSearchParams()
    if (strategicInitiativeId) params.append('strategic_initiative_id', strategicInitiativeId.toString())
    if (activeOnly) params.append('active_only', 'true')
    const queryString = params.toString()
    return this.request(`/api/v1/strategic-framework/operational-initiatives${queryString ? `?${queryString}` : ''}`)
  }

  /**
   * Get a specific operational initiative
   */
  async getOperationalInitiative(initiativeId: number): Promise<OperationalInitiative> {
    return this.request(`/api/v1/strategic-framework/operational-initiatives/${initiativeId}`)
  }

  /**
   * Get complete strategic framework cascade (all 3 levels)
   */
  async getStrategicCascade(activeOnly: boolean = true): Promise<StrategicCascade> {
    const params = activeOnly ? '?active_only=true' : '?active_only=false'
    return this.request(`/api/v1/strategic-framework/cascade${params}`)
  }

  /**
   * Get strategic objectives for dropdown selection
   */
  async lookupStrategicObjectives(): Promise<Array<{
    id: number
    code: string
    name: string
    display_name: string
    color_code?: string
  }>> {
    return this.request('/api/v1/strategic-framework/lookup/strategic-objectives')
  }

  /**
   * Get strategic initiatives for dropdown selection
   */
  async lookupStrategicInitiatives(strategicObjectiveId?: number): Promise<Array<{
    id: number
    code: string
    name: string
    strategic_objective_id: number
  }>> {
    const params = strategicObjectiveId ? `?strategic_objective_id=${strategicObjectiveId}` : ''
    return this.request(`/api/v1/strategic-framework/lookup/strategic-initiatives${params}`)
  }

  /**
   * Get operational initiatives for dropdown selection
   */
  async lookupOperationalInitiatives(strategicInitiativeId?: number): Promise<Array<{
    id: number
    code: string
    name: string
    strategic_initiative_id: number
  }>> {
    const params = strategicInitiativeId ? `?strategic_initiative_id=${strategicInitiativeId}` : ''
    return this.request(`/api/v1/strategic-framework/lookup/operational-initiatives${params}`)
  }
}

// Export singleton instance
export const performanceService = new PerformanceService()

// Export role types for type safety
export type RoleType = 
  | 'lecturer' 
  | 'head-of-department' 
  | 'dean' 
  | 'registrar' 
  | 'finance-manager' 
  | 'hr-manager' 
  | 'student-affairs' 
  | 'marketing-manager'
